import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Bot, Save } from 'lucide-react';
import {
  adminGetCustomer,
  adminGetCustomerAgents,
  getServiceBusiness,
  getTwilioAccounts,
  upsertTwilioAccount,
} from '../../lib/api';

const collectEmbeddedTwilioAccounts = (payload) => {
  if (!payload || typeof payload !== 'object') return [];
  const out = [];
  const pushArray = (arr) => {
    if (Array.isArray(arr)) out.push(...arr);
  };
  pushArray(payload.twilio_config);
  pushArray(payload.twilio_accounts);
  pushArray(payload.twilio_mappings);
  pushArray(payload.accounts);
  if (payload.customer && typeof payload.customer === 'object') {
    pushArray(payload.customer.twilio_config);
  }
  if (payload.business && typeof payload.business === 'object') {
    pushArray(payload.business.twilio_config);
  }
  if (payload.twilio_account && typeof payload.twilio_account === 'object') {
    out.push(payload.twilio_account);
  }
  return out;
};

const collectAgentTwilioConfigs = (agents = []) => {
  const out = [];
  agents.forEach((ag) => {
    if (Array.isArray(ag.twilio_config)) out.push(...ag.twilio_config);
    else if (ag.twilio_account && typeof ag.twilio_account === 'object') out.push(ag.twilio_account);
    else if (ag.twilio_mapping && typeof ag.twilio_mapping === 'object') out.push(ag.twilio_mapping);
  });
  return out;
};

const normalizeTwilioAccountList = (raw) => {
  if (!raw) return [];
  const data = raw.data !== undefined ? raw.data : raw;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.twilio_config)) return data.twilio_config;
  if (Array.isArray(data.accounts)) return data.accounts;
  if (Array.isArray(data.twilio_accounts)) return data.twilio_accounts;
  return collectEmbeddedTwilioAccounts(data);
};

const pickAccountForAgent = (accounts, agentId) => {
  if (!accounts.length) return null;
  if (!agentId) return accounts[0];
  return accounts.find((a) => (a.agent_id || a.agentId) === agentId) || accounts[0];
};

const resolveAccountForAgent = (accounts, agents, agentId) => {
  let account = pickAccountForAgent(accounts, agentId);
  if (account || !agentId) return account;
  const ag = agents.find((a) => a.agent_id === agentId);
  if (!ag) return null;
  if (Array.isArray(ag.twilio_config)) {
    return pickAccountForAgent(ag.twilio_config, agentId);
  }
  if (ag.twilio_config && typeof ag.twilio_config === 'object') {
    return ag.twilio_config;
  }
  if (ag.twilio_account && typeof ag.twilio_account === 'object') return ag.twilio_account;
  if (ag.twilio_mapping && typeof ag.twilio_mapping === 'object') return ag.twilio_mapping;
  return null;
};

const accountToFormFields = (account) => {
  if (!account) {
    return {
      name: '',
      accountSid: '',
      authToken: '',
      authTokenDecryptionFailed: false,
      active: true,
    };
  }

  const legacyToken = account?.auth_token ?? account?.authToken;
  const hasTwilioAuthTokenKey =
    account && Object.prototype.hasOwnProperty.call(account, 'twilio_auth_token');
  const authTokenDecryptionFailed = Boolean(hasTwilioAuthTokenKey && account.twilio_auth_token === null);

  let authToken = '';
  if (account && !authTokenDecryptionFailed) {
    if (typeof account.twilio_auth_token === 'string') {
      authToken = account.twilio_auth_token;
    } else if (account.twilio_auth_token === undefined && typeof legacyToken === 'string') {
      authToken = legacyToken;
    }
  }

  return {
    name: account?.name || account?.friendly_name || account?.mapping_name || '',
    accountSid: account?.twilio_account_sid || account?.account_sid || account?.accountSid || '',
    authToken,
    authTokenDecryptionFailed,
    active: account ? account.active !== false : true,
  };
};

export default function ServiceBusinessTwilioConfigPage() {
  const { businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedAgentId = searchParams.get('agentId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const [agents, setAgents] = useState([]);
  const [twilioAccountsCache, setTwilioAccountsCache] = useState([]);
  const [form, setForm] = useState({
    name: '',
    agentId: '',
    accountSid: '',
    authToken: '',
    authTokenDecryptionFailed: false,
    active: true,
  });

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.agent_id === form.agentId) || null,
    [agents, form.agentId]
  );
  const isAdminRoute = location.pathname.startsWith('/admin/');
  const backPath = isAdminRoute ? `/admin/customers/${businessId}` : `/service/businesses/${businessId}`;

  const handleAgentChange = (value) => {
    const acc = resolveAccountForAgent(twilioAccountsCache, agents, value);
    const fields = accountToFormFields(acc);
    setForm((prev) => ({
      ...prev,
      ...fields,
      agentId: value,
    }));
  };

  useEffect(() => {
    const loadBusiness = async () => {
      try {
        setLoading(true);
        let businessData = null;
        let businessAgents = [];
        let envelope = {};

        if (isAdminRoute) {
          const [customerRes, agentsRes] = await Promise.all([
            adminGetCustomer(businessId),
            adminGetCustomerAgents(businessId),
          ]);
          envelope = customerRes?.data || {};
          const customer = envelope.customer || null;
          businessData = customer
            ? {
                ...customer,
                company_name: customer.company_name,
                name: customer.name,
              }
            : null;
          businessAgents = Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : [];
        } else {
          const response = await getServiceBusiness(businessId);
          envelope = response?.data || {};
          businessData = envelope.business || null;
          businessAgents = Array.isArray(envelope.agents) ? envelope.agents : [];
        }

        setBusiness(businessData);
        setAgents(businessAgents);

        const preferredAgentId =
          requestedAgentId && businessAgents.some((a) => a.agent_id === requestedAgentId)
            ? requestedAgentId
            : businessAgents[0]?.agent_id || '';

        let mergedList = [
          ...collectEmbeddedTwilioAccounts(envelope),
          ...collectAgentTwilioConfigs(businessAgents),
        ];

        if (preferredAgentId) {
          try {
            const twilioRes = await getTwilioAccounts({ agent_id: preferredAgentId });
            mergedList = [...mergedList, ...normalizeTwilioAccountList(twilioRes)];
          } catch {
            // GET /twilio/accounts may be absent or return 404 until backend implements it
          }
        }

        setTwilioAccountsCache(mergedList);

        let account = resolveAccountForAgent(mergedList, businessAgents, preferredAgentId);

        const fields = accountToFormFields(account);
        setForm({
          ...fields,
          agentId: preferredAgentId,
        });
      } catch (error) {
        toast.error('Failed to load business details');
        navigate(isAdminRoute ? '/admin/customers' : '/service/businesses');
      } finally {
        setLoading(false);
      }
    };

    loadBusiness();
  }, [businessId, navigate, requestedAgentId, isAdminRoute]);

  const handleSave = async () => {
    const name = form.name.trim();
    const accountSid = form.accountSid.trim();
    const authToken = form.authToken.trim();

    if (!name) {
      toast.error('Name is required');
      return;
    }
    if (!form.agentId) {
      toast.error('Please select an agent');
      return;
    }
    if (!accountSid) {
      toast.error('Twilio Account SID is required');
      return;
    }
    if (!authToken) {
      toast.error(
        form.authTokenDecryptionFailed
          ? 'Enter a Twilio auth token (stored token could not be decrypted).'
          : 'Twilio Auth Token is required'
      );
      return;
    }

    const payload = {
      name,
      active: form.active,
      agentId: form.agentId,
      agent_id: form.agentId,
      accountSid,
      twilio_account_sid: accountSid,
      authToken,
      twilio_auth_token: authToken,
    };

    setSaving(true);
    try {
      await upsertTwilioAccount(payload);
      toast.success('Twilio configuration saved');
      setForm((prev) => ({ ...prev, authTokenDecryptionFailed: false }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to save Twilio configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card><CardContent className="p-6"><Skeleton className="h-72" /></CardContent></Card>
      </div>
    );
  }

  if (!business) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="service-business-twilio-config-page">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" onClick={() => navigate(backPath)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Business
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-600" />
            Twilio Configuration
          </CardTitle>
          <CardDescription>
            Configure Twilio account mapping for {business.company_name || business.name}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2">
            <Label htmlFor="twilio-name">Mapping Name *</Label>
            <Input
              id="twilio-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Sydney Main Account"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="twilio-agent">Agent *</Label>
            <Select value={form.agentId} onValueChange={handleAgentChange}>
              <SelectTrigger id="twilio-agent">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.agent_id} value={agent.agent_id}>
                    {agent.name || agent.agent_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAgent && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Bot className="w-3 h-3" />
                Agent ID: {selectedAgent.agent_id}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="twilio-account-sid">Twilio Account SID *</Label>
            <Input
              id="twilio-account-sid"
              value={form.accountSid}
              onChange={(e) => setForm((prev) => ({ ...prev, accountSid: e.target.value }))}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="twilio-auth-token">Twilio Auth Token *</Label>
            {form.authTokenDecryptionFailed && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                The stored auth token could not be decrypted (missing or invalid{' '}
                <code className="text-xs">TWILIO_TOKEN_ENCRYPTION_KEY</code> or corrupt payload). Enter a
                new token to save.
              </p>
            )}
            <Input
              id="twilio-auth-token"
              type="password"
              value={form.authToken}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  authToken: v,
                  ...(v.length > 0 ? { authTokenDecryptionFailed: false } : {}),
                }));
              }}
              placeholder={
                form.authTokenDecryptionFailed
                  ? 'Enter a new Twilio auth token'
                  : 'Enter Twilio auth token'
              }
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <div>
              <p className="font-medium text-slate-900">Active mapping</p>
              <p className="text-sm text-slate-500">Enable or disable this Twilio account mapping.</p>
            </div>
            <Switch
              checked={form.active}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, active: checked }))}
            />
          </div>

          {agents.length === 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              This business has no agents yet. Create an agent first, then configure Twilio mapping.
            </p>
          )}

          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || agents.length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Twilio Configuration'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
